/**
 * The `people` namespace, in English — C10 and its two drawers (`KLQ-20260819-004`).
 *
 * Its own namespace rather than more of `projects`, per the rule in `lib/i18n/catalog.ts`:
 * namespaces are split by screen family so a translator can finish one family at a time, and
 * the project's team screen is a different family from the workspace's project list.
 *
 * Two pieces of copy here are load-bearing rather than decorative, and neither should be
 * softened without reading the reasoning first:
 *
 * - `invite.error.verifyEmail` / `verifyDomain` are the browser's half of `route_to_raise`.
 *   They name what is blocked and what raises it, and they promise no button, because the
 *   screens those routes point at do not exist yet and there is no resend endpoint at all.
 * - `legend.workspaceNote` exists because the design draws an `owner` role the product has
 *   never had. Project roles are the four below; running the firm is a workspace fact.
 */

export const peopleEn = {
  'page.title': 'People & roles',
  'page.subtitle': 'Everybody working on this project, from every company involved.',

  'action.invite': 'Invite',
  'action.addColleague': 'Add colleague',
  'action.revoke': 'Revoke',

  'status.loading': 'Loading the team…',

  'group.own': 'Your company',
  'group.independent': 'Independent',
  'group.count': '{count, plural, one {# person} other {# people}}',
  'group.pending': 'Invited, not yet joined',
  'group.pendingHint': 'These people have been emailed a link and have not used it yet.',

  'row.you': 'You',
  // A membership whose account has been erased. The row survives so the team still explains
  // who holds which authority, without naming an erased person.
  'row.redacted': 'Removed account',
  'row.roleLabel': 'Role of {name}',
  'row.remove': 'Remove {name} from this project',
  'row.scope.full': 'Whole project',
  'row.scope.zones': '{count, plural, one {# zone} other {# zones}}',
  'row.notAccepted': 'Not joined yet',
  'row.expires': 'Link expires {date}',

  'empty.explains': 'Invite an engineer, a contractor or the client. They will see only this project.',
  'empty.title': 'Just you in here',
  'empty.body':
    'Invite the engineers, contractors and clients who need to see this project. They keep their own account and their own company. They are not added to yours.',

  'legend.title': 'What each role can do',
  'legend.architect':
    'Runs the project: publishes versions, requests reviews, edits the hierarchy and the naming convention.',
  'legend.engineer': 'Publishes into their own discipline, comments, and works on tasks.',
  'legend.contractor': 'Reads the drawings they are scoped to, reports issues, and works on their tasks.',
  'legend.clientReadonly': 'Reads the project and decides the approvals they are named on.',
  'legend.workspaceNote':
    'There is no project owner role: admins and managers of the company that owns a project reach every one of its projects without appearing in this list.',

  'role.architect': 'Architect',
  'role.engineer': 'Engineer',
  'role.contractor': 'Contractor',
  'role.clientReadonly': 'Client (read-only)',

  'drawer.close': 'Close',
  'drawer.cancel': 'Cancel',

  'invite.title': 'Invite to this project',
  'invite.subtitle':
    'They receive a link by email. If they have no account yet, one is created when they accept, in their own company and never in yours.',
  'invite.send': 'Send invitation',
  'invite.field.email.label': 'Email address',
  'invite.field.email.placeholder': 'engineer@structures.example',
  'invite.field.role.label': 'Role on this project',
  'invite.field.role.hint': 'Only for this project. The same person can hold a different role on another.',
  'invite.error.emailRequired': 'Enter the email address to invite.',
  'invite.error.alreadyInvited': 'That address is already on this project or already invited.',
  'invite.error.verifyEmail':
    'Your company has not confirmed its own email address yet, so invitations to new recipients cannot be sent. Confirm the address from the message sent when the company was created.',
  'invite.error.verifyDomain':
    "Your company has reached today's limit for inviting people it has not contacted before. The limit rises as the account establishes itself, and people you have invited before can still be invited today.",
  'invite.error.quota': 'A sending limit on your company prevents this invitation right now.',

  'add.title': 'Add a colleague',
  'add.subtitle': 'Somebody from your own company who already has an account.',
  'add.confirm': 'Add to project',
  'add.confirmMany': 'Add {count} people',
  'add.pickLabel': 'Colleagues in your company',
  'add.field.role.label': 'Role on this project',
  'add.field.role.hint': 'Independent of what they are in your company.',
  'add.empty': 'Everybody in your company is already on this project.',
  'add.error.load': 'Your company’s member list could not be loaded.',

  // The roster of firms (`KLQ-20260820-007`). Two pieces of copy carry the whole section:
  // `party.add.subtitle` promises nothing is sent, because nothing is, and `party.link.admits`
  // is the sentence that stands between a person and an act nobody can take back.
  'party.section.title': 'Organisations on this project',
  'party.section.hint':
    'Every firm doing work here, whether or not they use Kalqa.',
  'party.action.add': 'Add organisation',
  'party.action.link': 'Link {name} to a company',
  'party.action.archive': 'Archive {name}',
  'party.status.loading': 'Loading the organisations…',
  'party.explains': 'An organisation is a firm working on the project, even without a Kalqa account.',
  'party.empty.title': 'Nobody on this project yet',
  'party.empty':
    'No organisations recorded yet. Add the engineers, contractors and clients doing work here, by name. They need no account and nothing is sent to them.',
  'party.mark.offSystem': 'Off-system',
  'party.mark.linked': 'Uses Kalqa',
  'party.members.count': '{count, plural, =0 {Nobody here yet} one {# person here} other {# people here}}',
  'party.members.none': 'Nobody here',

  'party.add.title': 'Add an organisation',
  'party.add.subtitle':
    'A name is all this needs. No account is created, no invitation is sent and nothing is emailed to anybody. Inviting their people is a separate step.',
  'party.add.confirm': 'Record organisation',
  'party.field.directory.label': 'From the studio’s directory',
  'party.field.directory.none': 'A firm not in the directory yet',
  'party.field.directory.hint':
    'Firms you already work with. Picking one keeps the two projects talking about the same firm.',
  'party.field.name.label': 'Name of the organisation',
  'party.field.name.placeholder': 'Elektro s.r.o.',
  'party.field.name.hint': 'As the studio writes it. It can be corrected later.',
  'party.field.note.label': 'Note (optional)',
  'party.field.note.placeholder': 'Electrics, on site from March',
  'party.field.note.hint': 'For your own team. Nobody outside this project reads it.',

  'party.link.pickLabel': 'Company to link',
  'party.link.pickHint': 'Only companies whose people are already on this project.',
  'party.link.placeholder': 'Choose a company…',
  'party.link.loading': 'Loading the companies…',
  'party.link.none':
    'No company can be linked yet. A company becomes linkable once one of its people is on this project.',
  'party.link.error.load': 'The list of companies could not be loaded.',
  'party.link.admits':
    'Linking {party} to {firm} lets {firm} into every task this organisation is already assigned. It cannot be undone: correcting a mistake means archiving this organisation and recording another.',
  'party.link.internal':
    'Comments written before now stay internal. Only what is written after the link is visible to them.',
  'party.link.confirm': 'Link the company',

  'party.error.load': 'The organisations could not be loaded.',
  'party.error.nameRequired': 'Enter the name of the organisation.',
  'party.error.duplicate': 'That organisation is already on this project.',
  'party.error.conflict': 'That company is already linked to another organisation on this project.',

  // Wave 2: a firm's contacts and roles. `party.contact.noMail` sits under the e-mail field
  // because that is the field a person reads as "they will be notified" — and nothing is sent.
  'party.contact.label': 'Contacts',
  'party.contact.loading': 'Loading contacts and roles…',
  'party.contact.fromDirectory': 'From the studio’s directory',
  'party.contact.none': 'No contacts recorded.',
  'party.contact.action.add': 'Add contact',
  'party.contact.delete': 'Delete {name}',
  'party.contact.add.title': 'Add a contact at {party}',
  'party.contact.add.subtitle':
    'Somebody to phone at this organisation. They get no account and no invitation, and nothing is emailed to them.',
  'party.contact.add.confirm': 'Record contact',
  'party.contact.field.name.label': 'Name',
  'party.contact.field.name.placeholder': 'Ján Novák',
  'party.contact.field.phone.label': 'Phone (optional)',
  'party.contact.field.phone.placeholder': '+421 900 123 456',
  'party.contact.field.email.label': 'E-mail (optional)',
  'party.contact.field.email.placeholder': 'jan.novak@elektro.sk',
  'party.contact.noMail': 'Kept for your records only. Kalqa never sends anything to this address.',
  'party.contact.error.nameRequired': 'Enter the contact’s name.',
  'party.contact.error.email': 'That does not look like an e-mail address.',
  'party.contact.error.load': 'The contacts could not be loaded.',
  'party.role.label': 'Roles on this project',
  'party.role.add': 'Add a role…',
  'party.role.remove': 'Remove the role {role}',
  'party.role.unknown': 'A role no longer in the list',
  'party.role.error.load': 'The list of roles could not be loaded.',

  'error.load': 'The team could not be loaded.',
  'error.alreadyMember': 'That person is already on this project.',
  'error.forbidden': 'You are not allowed to make that change.',
  'error.gone': 'That person is no longer on this project.',
  'error.rateLimited': 'Too many changes at once. Wait a moment and try again.',
  'error.validation': 'That change was refused. Check the values and try again.',
  'error.unexpected': 'Something went wrong. Try again.',
} as const;

export type PeopleMessageKey = keyof typeof peopleEn;
